import { GraphQLServerError } from "~/common/graphql/GraphQLServerError";
import { ResponseErrorTypes } from "~/EasyProjectsCommon/EpEnums";
import { commonResx } from "~/resxDist/commonResx";
import { ApiErrorTypes, ErrorResponse, UserFriendlyExcBody } from "./errorTypes";

type Xhr = Partial<JQuery.jqXHR & { responseTextFromBlob?: string }> | GraphQLServerError;
export const isGraphQlError = (x: Xhr): x is GraphQLServerError => x instanceof GraphQLServerError;
export const isJQueryXhr = (x: Xhr): x is JQuery.jqXHR => !isGraphQlError(x);

export const getResponseBody = (x: Xhr): ErrorResponse =>
    isGraphQlError(x)
        ? {
              ErrorType: x.code,
              EntityName: x.entityName,
              ObjectKey: x.objectKey,
              ErrorKey: x.errorKey as keyof typeof commonResx,
          }
        : getAjaxResponse(x as JQuery.jqXHR);

const getAjaxResponse = (x: JQuery.jqXHR & { responseTextFromBlob?: string }) => {
    let response = x.responseJSON;

    if (!response) {
        try {
            response = JSON.parse(x.responseTextFromBlob || x.responseText || "{}");
        } catch (e) {
            // ignore errors because sometimes response might be html (for example on unauthorized response)
        }
    }

    return response || {};
};

export const getErrorType = (x: Xhr): ApiErrorTypes => getResponseBody(x).ErrorType;

export const isObjectNotFoundError = (x: Xhr) => getErrorType(x) === ApiErrorTypes.ObjectNotFound;
export const isUserFriendlyError = (e: Xhr) => getErrorType(e) === ApiErrorTypes.UserFriendlyError;

export const isUserFriendlyErrorWithCode = (xhr: Xhr, errorCode: string) => {
    if (isUserFriendlyError(xhr)) {
        let body = getResponseBody(xhr) as UserFriendlyExcBody;
        return body.ErrorKey === errorCode;
    }

    return false;
};

export const isServerVersionMismatchError = (x: Xhr) => getErrorType(x) === ResponseErrorTypes.ServerVersionException;
export const isGatewayTimeoutError = (x: Xhr) => isJQueryXhr(x) && x.status === 504;
export const isOfflineError = (x: Xhr) =>
    isJQueryXhr(x) && x.status === 0 && x.readyState === 0 && x.statusText === "error" && !navigator.onLine;

export const isUpgradePlanRequiredError = (x: Xhr) => getErrorType(x) === ResponseErrorTypes.UpgradePlanRequired;
